import React, { useCallback } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { Menu, Position, Tooltip } from 'evergreen-ui';
import { ModalLinkIcon } from 'public/icons';

import { configurations, tabs, path } from 'pages/routes';

import { useLogout } from 'resources/auth/hooks';

import styles from './styles.module.scss';

const TabItem = ({ tab, disabled, user, company, closeMenu }) => {
  const router = useRouter();

  const { mutateAsync: logout } = useLogout();

  const isActiveTab = configurations(user, company)
    .find((item) => item.path === router.route && item.sideBarTab === tab.label);

  const onNavigateToPage = useCallback((pagePath) => {
    router.push(pagePath);
  }, [router]);

  const onLogOut = useCallback(async () => {
    await logout();
  }, [logout]);

  const onTabClick = useCallback(async () => {
    if (tab.link) {
      isExternalLink ? window.open(tab.link, '_blank') : onNavigateToPage(tab.link);
      return;
    }

    if (tab.label === tabs.sideBarTabs.LOG_OUT) {
      await onLogOut();
    }

    if (tab.label === tabs.sideBarTabs.LOG_IN) {
      router.push(path.signIn);
    }
  }, [router, onNavigateToPage, onLogOut, tab, isExternalLink]);

  const isExternalLink = tab.link && typeof tab.link === 'string' && tab.link.startsWith('http');
  const isMenuItem = closeMenu !== undefined;

  return (
    <Tooltip 
      isShown={tab.tooltip ? undefined : false} 
      content={tab.tooltip} 
      position={Position.RIGHT} 
      showDelay={300}
    >
      {isMenuItem ? (
        <>
          {tab.label === tabs.sideBarTabs.LOG_OUT && <Menu.Divider />}
          <Menu.Item
            className={cn({
              [styles.activeMenuItemTab]: isActiveTab,
              [styles.disabledTab]: disabled,
            })}
            icon={tab.icon()}
            onSelect={async () => {
              await onTabClick();
              closeMenu();
            }}
            secondaryText={isExternalLink ? <ModalLinkIcon /> : null}
            aria-hidden
          >
            {tab.label}
          </Menu.Item>
        </>
      ) : (
        <div
          className={cn({
            [styles.tab]: true,
            [styles.activeTab]: isActiveTab,
            [styles.disabledTab]: disabled && tab.label !== tabs.sideBarTabs.LOG_IN,
          })}
          onClick={onTabClick}
          aria-hidden
        >
          {tab.icon()}
          <p>{tab.label}</p>
        </div>
      )}
    </Tooltip>
  );
};

TabItem.propTypes = {
  tab: PropTypes.shape({
    link: PropTypes.string,
    label: PropTypes.string.isRequired,
    tooltip: PropTypes.string,
    isOnBottom: PropTypes.bool,
    icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  }).isRequired,
  user: PropTypes.shape({}).isRequired,
  company: PropTypes.shape({}),
  disabled: PropTypes.bool,
  closeMenu: PropTypes.func,
};

TabItem.defaultProps = {
  disabled: false,
  closeMenu: undefined,
};

export default React.memo(TabItem);
